import React, { useState } from 'react';
import { Loader2, Percent, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { adminAPI } from '../services/api';

const ROUNDINGS = [['cent', 'Au centime'], ['99', 'Finir en ,99'], ['90', 'Finir en ,90'], ['50', 'Finir en ,50'], ['unit', "À l'euro"]];
const inputCls = 'mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white';

const preview = (price, percent, rounding) => {
  const v = price * (1 + percent / 100);
  if (rounding === 'cent') return Math.round(v * 100) / 100;
  if (rounding === 'unit') return Math.max(1, Math.round(v));
  const ending = { 99: 0.99, 90: 0.9, 50: 0.5 }[rounding];
  let lower = Math.floor(v) + ending;
  if (lower > v) lower -= 1;
  const upper = lower + 1;
  return Math.max(ending, v - lower <= upper - v ? lower : upper);
};

const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
const defaultStart = () => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return toLocalInput(d); };
const defaultEnd = () => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); d.setDate(d.getDate() + 7); return toLocalInput(d); };

export const BulkPriceDialog = ({ open, onClose, selectedIds, sampleProducts = [], onDone }) => {
  const [mode, setMode] = useState('now');
  const [percent, setPercent] = useState(-10);
  const [rounding, setRounding] = useState('cent');
  const [keepOriginal, setKeepOriginal] = useState(true);
  const [label, setLabel] = useState('');
  const [startAt, setStartAt] = useState(defaultStart);
  const [endAt, setEndAt] = useState(defaultEnd);
  const [busy, setBusy] = useState(false);
  const pct = Number(percent) || 0;
  const scheduling = mode === 'schedule';
  const canApply = scheduling ? pct < 0 && label.trim() && startAt && endAt && endAt > startAt : pct !== 0;

  const apply = async () => {
    setBusy(true);
    try {
      if (scheduling) {
        const r = await adminAPI.createPromo({ label: label.trim(), product_ids: selectedIds, percent: pct, rounding, start_at: new Date(startAt).toISOString(), end_at: new Date(endAt).toISOString() });
        toast.success(r.started_now ? `Promo « ${r.label} » démarrée immédiatement sur ${r.applied_count} produit(s)` : `Promo « ${r.label} » programmée pour ${r.product_count} produit(s)`);
      } else {
        const r = await adminAPI.bulkPrice(selectedIds, pct, rounding, keepOriginal);
        toast.success(`${r.message}${r.skipped?.length ? ` · ${r.skipped.length} ignoré(s)` : ''}`);
      }
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opération impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-price-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Percent size={18} className="text-orange-500" /> Prix groupé — {selectedIds.length} produit(s)</DialogTitle>
          <DialogDescription>Un pourcentage négatif applique une remise, un pourcentage positif une hausse. Chaque changement est tracé et annulable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 text-sm" role="tablist">
            <button type="button" data-testid="bulk-price-mode-now" onClick={() => setMode('now')} className={`px-3 py-1.5 rounded ${!scheduling ? 'bg-white shadow font-medium text-gray-900' : 'text-gray-500'}`}>Appliquer maintenant</button>
            <button type="button" data-testid="bulk-price-mode-schedule" onClick={() => { setMode('schedule'); if (pct > 0) setPercent(-10); }} className={`px-3 py-1.5 rounded inline-flex items-center gap-1 ${scheduling ? 'bg-white shadow font-medium text-gray-900' : 'text-gray-500'}`}><CalendarClock size={14} /> Programmer une promo</button>
          </div>
          {scheduling && (
            <div className="space-y-3 rounded-md border border-orange-200 bg-orange-50/50 p-3" data-testid="bulk-price-schedule-fields">
              <label className="block text-sm text-gray-700">Nom de la promotion
                <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Ex. Soldes d'été" maxLength={80} data-testid="bulk-price-promo-label" className={inputCls} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">Début
                  <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} data-testid="bulk-price-promo-start" className={inputCls} />
                </label>
                <label className="text-sm text-gray-700">Fin (retour aux prix normaux)
                  <input type="datetime-local" value={endAt} min={startAt} onChange={e => setEndAt(e.target.value)} data-testid="bulk-price-promo-end" className={inputCls} />
                </label>
              </div>
              <p className="text-xs text-gray-500">La promo démarre et se termine automatiquement (vérification toutes les heures). L'ancien prix est affiché barré pendant la promo.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm text-gray-700">{scheduling ? 'Remise (%)' : 'Variation (%)'}
              <input type="number" step="0.5" min={-90} max={scheduling ? -0.5 : 500} value={percent} onChange={e => setPercent(e.target.value)} data-testid="bulk-price-percent" className={inputCls} />
            </label>
            <label className="text-sm text-gray-700">Arrondi
              <select value={rounding} onChange={e => setRounding(e.target.value)} data-testid="bulk-price-rounding" className={inputCls}>
                {ROUNDINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {(scheduling ? [-50, -40, -30, -20, -10, -5] : [-30, -20, -10, -5, 5, 10, 20]).map(v => (
              <button key={v} type="button" onClick={() => setPercent(v)} data-testid={`bulk-price-preset-${v}`} className={`text-xs px-2.5 py-1 rounded-full border ${pct === v ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300 text-gray-600 hover:border-orange-400'}`}>{v > 0 ? `+${v}` : v} %</button>
            ))}
          </div>
          {!scheduling && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={keepOriginal} onChange={e => setKeepOriginal(e.target.checked)} data-testid="bulk-price-keep-original" className="mt-0.5 rounded border-gray-300 text-orange-500" />
              <span>En cas de remise, conserver l'ancien prix comme prix barré (affiche le badge -x % sur le site)</span>
            </label>
          )}
          {sampleProducts.length > 0 && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs" data-testid="bulk-price-preview">
              <p className="font-medium text-gray-700 mb-1.5">Aperçu</p>
              <ul className="space-y-1">
                {sampleProducts.slice(0, 4).map(p => (
                  <li key={p.id} className="flex justify-between gap-3"><span className="truncate text-gray-600">{p.name}</span><span className="whitespace-nowrap"><span className="text-gray-400 line-through mr-1">{Number(p.price).toFixed(2)} €</span><strong className={pct < 0 ? 'text-green-700' : 'text-orange-700'}>{preview(Number(p.price), pct, rounding).toFixed(2)} €</strong></span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="bulk-price-cancel">Annuler</Button>
          <Button type="button" onClick={apply} disabled={busy || !canApply} data-testid="bulk-price-apply" className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy && <Loader2 size={14} className="mr-1 animate-spin" />} {scheduling ? `Programmer ${pct} %` : `Appliquer ${pct > 0 ? `+${pct}` : pct} %`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
